import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, SlidersHorizontal, Heart, MessageCircle, Star, X, Sparkles, ShieldOff, Pencil } from "lucide-react";
import type { Character } from "../services/api";
import { getCharacters, toggleFavorite, getFavorites, CHARACTER_CATEGORIES, SORT_OPTIONS } from "../services/api";
import { ImportCharacterButton, CharacterExportImport } from "./CharacterExportImport";

interface LobbyProps {
  onSelectCharacter: (char: Character) => void;
  onEditCharacter?: (char: Character) => void;
}

export function CharacterLobby({ onSelectCharacter, onEditCharacter }: LobbyProps) {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [sortBy, setSortBy] = useState("featured");
  const [showFilters, setShowFilters] = useState(false);
  const [nsfwEnabled, setNsfwEnabled] = useState(true);

  useEffect(() => {
    loadCharacters();
    loadFavorites();
  }, [activeCategory, sortBy, nsfwEnabled]);

  async function loadCharacters() {
    setLoading(true);
    try {
      const data = await getCharacters({
        category: activeCategory,
        search: search || undefined,
        sort: sortBy,
        nsfw: nsfwEnabled,
      });
      setCharacters(data);
    } catch (e) {
      console.error("加载角色失败:", e);
    } finally {
      setLoading(false);
    }
  }

  async function loadFavorites() {
    try {
      const favs = await getFavorites();
      setFavorites(favs.map((f) => f.id));
    } catch {}
  }

  useEffect(() => {
    const timer = setTimeout(loadCharacters, 300);
    return () => clearTimeout(timer);
  }, [search]);

  async function handleToggleFavorite(charId: string, e: React.MouseEvent) {
    e.stopPropagation();
    try {
      const result = await toggleFavorite(charId);
      setFavorites((prev) =>
        result.action === "favorite" ? [...prev, charId] : prev.filter((id) => id !== charId)
      );
    } catch {}
  }

  // 如果是收藏分类，用收藏数据
  const displayCharacters = useMemo(() => {
    if (activeCategory === "favorites") {
      return characters.filter((c) => favorites.includes(c.id));
    }
    return characters;
  }, [characters, activeCategory, favorites]);

  return (
    <div className="h-full flex flex-col bg-slate-950">
      {/* 顶部搜索栏 */}
      <div className="px-6 pt-6 pb-4 flex-shrink-0">
        <div className="flex items-center gap-3 mb-4">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-rose-400" />
            角色大厅
          </h1>
          <span className="text-xs text-slate-500 bg-slate-800 px-2 py-1 rounded-full">
            {displayCharacters.length} 个角色
          </span>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索名称、描述、标签、性格、背景..."
              className="w-full bg-slate-800/50 border border-slate-700 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500/50 transition-colors"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2.5 rounded-xl border transition-colors ${showFilters ? "bg-rose-500/20 border-rose-500/50 text-rose-400" : "bg-slate-800/50 border-slate-700 text-slate-400 hover:text-white"}`}
          >
            <SlidersHorizontal className="w-4 h-4" />
          </button>
          {/* NSFW 开关 */}
          <button
            onClick={() => setNsfwEnabled(!nsfwEnabled)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all ${
              nsfwEnabled
                ? "bg-rose-500/20 border-rose-500/50 text-rose-400"
                : "bg-slate-800/50 border-slate-700 text-slate-500 hover:text-slate-300"
            }`}
          >
            <ShieldOff className="w-3.5 h-3.5" />
            {nsfwEnabled ? "NSFW ON" : "NSFW"}
          </button>
          <ImportCharacterButton onImported={() => loadCharacters()} />
        </div>
      </div>

      {/* 分类 + 排序 */}
      <div className="px-6 pb-3 flex-shrink-0">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {CHARACTER_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                activeCategory === cat.id
                  ? "bg-rose-500 text-white"
                  : "bg-slate-800/50 text-slate-400 hover:bg-slate-800 hover:text-slate-200 border border-slate-700"
              }`}
            >
              <span>{cat.icon}</span>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>

        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-800">
                <span className="text-xs text-slate-500">排序：</span>
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setSortBy(opt.id)}
                    className={`px-2.5 py-1 rounded-lg text-xs transition-colors ${
                      sortBy === opt.id
                        ? "bg-purple-500/20 text-purple-400 border border-purple-500/30"
                        : "text-slate-500 hover:text-slate-300"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 角色卡片网格 */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="flex gap-1">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={{ y: [0, -8, 0] }}
                  transition={{ repeat: Infinity, duration: 0.6, delay: i * 0.15 }}
                  className="w-2 h-2 rounded-full bg-rose-400"
                />
              ))}
            </div>
          </div>
        ) : displayCharacters.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-slate-500">
            <Search className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-sm">没有找到角色</p>
            <p className="text-xs mt-1">试试其他关键词或分类</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <AnimatePresence>
              {displayCharacters.map((char, index) => (
                <motion.div
                  key={char.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -4, scale: 1.02 }}
                  onClick={() => onSelectCharacter(char)}
                  className="group relative bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden cursor-pointer hover:border-rose-500/30 transition-all"
                >
                  {/* 头像区域 */}
                  <div className="relative h-40 bg-gradient-to-b from-slate-800 to-slate-900 flex items-center justify-center overflow-hidden">
                    <img
                      src={char.avatar}
                      alt={char.name}
                      className="w-24 h-24 rounded-full object-cover border-2 border-slate-700 group-hover:border-rose-500/50 transition-colors"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    {/* 精选标记 */}
                    {char.featured && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-xs text-amber-400 font-medium">
                        ⭐ 精选
                      </div>
                    )}
                    {/* 收藏按钮 */}
                    <div className="absolute top-2 right-2 flex flex-col gap-1">
                      {char.nsfw && nsfwEnabled && (
                        <span className="px-1.5 py-0.5 rounded-full bg-rose-500/80 text-[9px] text-white font-bold leading-none">
                          NSFW
                        </span>
                      )}
                      {onEditCharacter && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onEditCharacter(char); }}
                          className="p-1.5 rounded-full bg-black/30 backdrop-blur-sm text-white/60 hover:text-blue-400 transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={(e) => handleToggleFavorite(char.id, e)}
                        className="p-1.5 rounded-full bg-black/30 backdrop-blur-sm text-white/60 hover:text-rose-400 transition-colors"
                      >
                        <Heart className={`w-3.5 h-3.5 ${favorites.includes(char.id) ? "fill-rose-400 text-rose-400" : ""}`} />
                      </button>
                    </div>
                  </div>

                  {/* 信息区域 */}
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-sm font-semibold text-white truncate">{char.name}</h3>
                      <div className="flex items-center gap-0.5">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        <span className="text-xs text-slate-400">{char.rating}</span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 line-clamp-2 mb-2">{char.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1 flex-wrap">
                        {char.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="px-1.5 py-0.5 rounded bg-slate-800 text-[10px] text-slate-400">
                            {tag}
                          </span>
                        ))}
                        {char.tags.length > 2 && (
                          <span className="px-1.5 py-0.5 rounded bg-slate-800 text-[10px] text-slate-500">
                            +{char.tags.length - 2}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-500">
                        <CharacterExportImport character={char} onImported={loadCharacters} />
                        <span className="flex items-center gap-0.5">
                          <MessageCircle className="w-3 h-3" />
                          {(char.conversations / 1000).toFixed(1)}k
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
